import { ThemeToggle } from '../components/theme/ThemeToggle';
import { LegalBackLink } from '../components/legal/LegalBackLink';
import { Helmet } from 'react-helmet-async';

function TermsOfUse() {
  return (
    <div className="min-h-screen bg-base-100">
      <Helmet>
        <meta name="robots" content="noindex, follow" />
      </Helmet>
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header with back button */}
        <div className="mb-8">
          <div className="mb-6 flex items-center justify-between gap-4">
            <LegalBackLink />
            {/* Sun/moon (dark mode D9, msg 7524): absent until dark is available (D5) */}
            <ThemeToggle testId="theme-toggle-header" />
          </div>
          <h1 className="text-4xl font-bold text-base-content mb-2">Terms of Use</h1>
          <p className="text-base-content/60">Last updated: June 26, 2026</p>
        </div>

        {/* Content */}
        <div className="bg-(--ee-surface) border border-(--ee-tw-gray-200-line) rounded-2xl p-8 prose prose-sm max-w-none">
          <section className="mb-8">
            <p className="text-base-content/80 leading-relaxed font-semibold">
              PLEASE READ THIS END USER LICENSE AGREEMENT CAREFULLY BEFORE DOWNLOADING, INSTALLING,
              OR USING THE EMPATHEASE APPLICATION. BY DOWNLOADING, INSTALLING, OR USING THE
              APPLICATION, YOU AGREE TO BE BOUND BY THESE TERMS. IF YOU DO NOT AGREE, DO NOT INSTALL
              OR USE THE APPLICATION.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-base-content mb-4">1. Parties</h2>
            <p className="text-base-content/80 leading-relaxed">
              This Agreement is between you (&quot;User&quot; or &quot;You&quot;) and the EmpathEase
              team (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;), the developer and publisher
              of the EmpathEase application (&quot;App&quot;). This Agreement governs your use of
              the App on iOS devices distributed through the Apple App Store, on Android devices
              distributed through the Google Play Store, and on the web at{' '}
              <a
                href="https://empathease.app"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline"
              >
                https://empathease.app
              </a>
              .
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-base-content mb-4">2. Grant of License</h2>
            <p className="text-base-content/80 leading-relaxed mb-3">
              Subject to your compliance with this Agreement, EmpathEase grants you a limited,
              non-exclusive, non-transferable, revocable license to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-base-content/80">
              <li>
                Download and install the App on iOS or Android devices that you own or control,
                and/or access the App via the web.
              </li>
              <li>Use the App for your personal, non-commercial purposes.</li>
              <li>Access features available under your current plan (Free, Premium, or Elite).</li>
            </ul>
            <p className="text-base-content/80 leading-relaxed mt-3">
              This license does not include the right to sublicense, resell, distribute, transfer,
              or commercially exploit the App or its content. Subscriptions are tied to your account
              and may not be shared with or transferred to another account.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-base-content mb-4">3. Restrictions</h2>
            <p className="text-base-content/80 leading-relaxed mb-3">
              You agree that you will NOT:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-base-content/80">
              <li>Copy, modify, adapt, translate, or create derivative works of the App.</li>
              <li>
                Reverse engineer, disassemble, decompile, or attempt to derive the source code of
                the App.
              </li>
              <li>
                Remove, alter, or obscure any proprietary notices (including copyright and trademark
                notices) in the App.
              </li>
              <li>Use the App for any unlawful, harmful, or fraudulent purpose.</li>
              <li>
                Attempt to gain unauthorized access to any portion of the App or its related
                systems.
              </li>
              <li>Use the App to infringe the intellectual property rights of any third party.</li>
              <li>
                Upload, transmit, or distribute malware, viruses, or any other harmful code through
                the App.
              </li>
              <li>Share, transfer, or sell your account or subscription to another person.</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-base-content mb-4">
              4. Plans &amp; Subscriptions
            </h2>

            <h3 className="text-xl font-semibold text-base-content mb-3 mt-4">
              4.1 Available Plans
            </h3>
            <p className="text-base-content/80 leading-relaxed mb-3">
              EmpathEase offers the following plans:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-base-content/80">
              <li>
                <strong>Free:</strong> Permanent access to a limited set of core features. No
                payment required.
              </li>
              <li>
                <strong>Premium Monthly:</strong> Full access to Premium features, billed monthly.
                Includes 2,500 credits per month.
              </li>
              <li>
                <strong>Premium Yearly:</strong> Full access to Premium features, billed annually.
              </li>
              <li>
                <strong>Elite Monthly:</strong> Full access to all features, billed monthly.
                Includes 30,000 credits per month (12x Premium).
              </li>
              <li>
                <strong>Elite Yearly:</strong> Full access to all features, billed annually.
              </li>
            </ul>

            <h3 className="text-xl font-semibold text-base-content mb-3 mt-4">4.2 Credits</h3>
            <p className="text-base-content/80 leading-relaxed">
              Credits are used to access AI-powered features within the App. Credits are allocated
              at the start of each billing period and reset monthly. Unused credits do not carry
              over to the next period. Credits have no monetary value and cannot be redeemed,
              transferred, or refunded.
            </p>

            <h3 className="text-xl font-semibold text-base-content mb-3 mt-4">
              4.3 Billing — iOS (Apple App Store)
            </h3>
            <p className="text-base-content/80 leading-relaxed">
              Subscriptions purchased on iOS are processed by Apple through your Apple ID. Payment
              is charged to your Apple ID at confirmation of purchase. Subscriptions automatically
              renew unless auto-renew is turned off at least 24 hours before the end of the current
              period. You can manage or cancel your subscription in your device&apos;s Settings
              under your Apple ID, or in your App Store account settings.
            </p>

            <h3 className="text-xl font-semibold text-base-content mb-3 mt-4">
              4.4 Billing — Android (Google Play)
            </h3>
            <p className="text-base-content/80 leading-relaxed">
              Subscriptions purchased through Android are processed by the Google Play Store. Your
              Google Play account will be charged at confirmation of purchase. Subscriptions
              automatically renew unless auto-renew is turned off at least 24 hours before the end
              of the current billing period. Manage your subscription anytime in your Google Play
              account settings.
            </p>

            <h3 className="text-xl font-semibold text-base-content mb-3 mt-4">4.5 Billing — Web</h3>
            <p className="text-base-content/80 leading-relaxed">
              Subscriptions purchased through the web are processed by Stripe. Your payment method
              will be charged at confirmation of purchase. Subscriptions automatically renew unless
              cancelled. You can manage or cancel your subscription anytime from your account
              settings on the EmpathEase website.
            </p>

            <h3 className="text-xl font-semibold text-base-content mb-3 mt-4">
              4.6 Cancellation &amp; Refunds
            </h3>
            <p className="text-base-content/80 leading-relaxed mb-3">
              You may cancel your subscription at any time. Cancellation takes effect at the end of
              the current billing period; you retain access to paid features until then.
            </p>
            <ul className="list-disc pl-6 space-y-2 text-base-content/80">
              <li>
                <strong>iOS:</strong> Refunds are handled by Apple under Apple&apos;s refund
                policies. EmpathEase cannot process refunds for App Store purchases; request them
                through Apple.
              </li>
              <li>
                <strong>Android:</strong> Refunds are subject to Google Play&apos;s refund policies.
                EmpathEase does not directly process refunds for Play Store purchases.
              </li>
              <li>
                <strong>Web:</strong> Refunds are subject to Stripe&apos;s refund policies. Contact
                us at{' '}
                <a href="mailto:support@empathease.app" className="text-primary underline">
                  support@empathease.app
                </a>{' '}
                for billing-related queries.
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-base-content mb-4">
              5. User Content &amp; Conduct
            </h2>
            <p className="text-base-content/80 leading-relaxed mb-3">
              If the App allows you to create or submit content (such as session notes or
              responses), you retain ownership of that content. By submitting content, you grant
              EmpathEase a limited, non-exclusive license to store and process it solely to provide
              the App&apos;s features to you.
            </p>
            <p className="text-base-content/80 leading-relaxed">
              You are solely responsible for ensuring that any content you create or share through
              the App does not violate any applicable laws or third-party rights.
            </p>

            <h3 className="text-xl font-semibold text-base-content mb-3 mt-4">
              5.1 Community Conduct &amp; Zero Tolerance
            </h3>
            <p className="text-base-content/80 leading-relaxed mb-3">
              EmpathEase includes live, person-to-person empathy sessions and circles. To keep them
              safe, we maintain a <strong>zero-tolerance policy</strong> for objectionable content
              and abusive behavior. You agree that you will not, during any session or anywhere in
              the App:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-base-content/80">
              <li>harass, threaten, bully, or intimidate another person;</li>
              <li>
                use hate speech or discriminatory, demeaning, sexually explicit, or abusive language
                toward others;
              </li>
              <li>
                share content that is illegal, violent, sexually explicit, or otherwise harmful;
              </li>
              <li>impersonate others, send spam, or solicit or advertise to other users;</li>
              <li>
                attempt to identify, contact, or track another user outside the App without their
                consent.
              </li>
            </ul>
            <p className="text-base-content/80 leading-relaxed mt-3">
              You can report objectionable behavior or content, and block other users, from within
              the App. We review reports and may remove content, issue warnings, and suspend or
              permanently terminate accounts that violate this policy, at our discretion and without
              prior notice. There is no place for objectionable content or abusive users on
              EmpathEase.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-base-content mb-4">6. Privacy &amp; Data</h2>
            <p className="text-base-content/80 leading-relaxed">
              Our Privacy Policy, available at{' '}
              <a
                href="https://empathease.app/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline"
              >
                https://empathease.app/privacy
              </a>
              , describes how we collect, use, store, and share information when you use the App. It
              is incorporated into this Agreement by reference. By using the App, you consent to the
              data practices described therein.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-base-content mb-4">
              7. Third-Party Services
            </h2>
            <p className="text-base-content/80 leading-relaxed">
              The App integrates with third-party services including Google Play (payments on
              Android), Stripe (payments on web), and cloud infrastructure providers. Your use of
              such services is subject to their respective terms and privacy policies. EmpathEase is
              not responsible for the practices or content of third-party services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-base-content mb-4">
              8. Intellectual Property
            </h2>
            <p className="text-base-content/80 leading-relaxed mb-3">
              The App, including its design, code, text, graphics, audio content, and underlying
              technology, is the property of EmpathEase and is protected by applicable intellectual
              property laws. All rights not expressly granted in this Agreement are reserved.
            </p>
            <p className="text-base-content/80 leading-relaxed">
              &quot;EmpathEase&quot; and related logos and marks are trademarks of EmpathEase.
              Nothing in this Agreement grants you any right to use EmpathEase trademarks, trade
              names, or service marks.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-base-content mb-4">
              9. Updates &amp; Modifications
            </h2>
            <p className="text-base-content/80 leading-relaxed mb-3">
              We may issue updates to the App from time to time. Continued use of the App after an
              update constitutes acceptance of the updated version. We reserve the right to modify,
              suspend, or discontinue any feature at any time, with or without notice.
            </p>
            <p className="text-base-content/80 leading-relaxed">
              We may also amend this Agreement at any time. Material changes will be communicated by
              updating the &quot;Last Updated&quot; date above or through an in-app notice.
              Continued use after changes constitutes acceptance of the revised Agreement.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-base-content mb-4">10. Disclaimers</h2>
            <p className="text-base-content/80 leading-relaxed mb-3 font-semibold">
              THE APP IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE,&quot; WITHOUT WARRANTY
              OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF
              MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
            </p>
            <p className="text-base-content/80 leading-relaxed mb-3">
              EmpathEase does not warrant that the App will be uninterrupted, error-free, or free of
              harmful components.
            </p>
            <p className="text-base-content/80 leading-relaxed">
              The App is not a substitute for professional mental health treatment, therapy, or
              medical advice. If you are experiencing a mental health crisis, please contact a
              qualified healthcare professional or emergency services immediately.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-base-content mb-4">
              11. Limitation of Liability
            </h2>
            <p className="text-base-content/80 leading-relaxed mb-3 font-semibold">
              TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, EMPATHEASE SHALL NOT BE LIABLE FOR
              ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM
              YOUR USE OF OR INABILITY TO USE THE APP.
            </p>
            <p className="text-base-content/80 leading-relaxed">
              In no event shall EmpathEase&apos;s total liability to you exceed the amount you paid
              to EmpathEase in the twelve (12) months preceding the claim, or $10 USD (whichever is
              greater).
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-base-content mb-4">12. Indemnification</h2>
            <p className="text-base-content/80 leading-relaxed">
              You agree to indemnify and hold harmless EmpathEase and its team members from any
              claims, damages, or expenses (including reasonable attorneys&apos; fees) arising from
              your violation of this Agreement or your use of the App.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-base-content mb-4">13. Governing Law</h2>
            <p className="text-base-content/80 leading-relaxed">
              This Agreement shall be governed by and construed in accordance with the laws of the
              United States. Any disputes shall be resolved through good-faith negotiation. If
              unresolved, disputes shall be subject to binding arbitration or the applicable courts
              of the United States.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-base-content mb-4">14. Termination</h2>
            <p className="text-base-content/80 leading-relaxed mb-3">
              This Agreement is effective until terminated. Your rights terminate automatically if
              you fail to comply with its terms. Upon termination, you must cease all use of the
              App. EmpathEase reserves the right to suspend or terminate your access at any time,
              with or without cause.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-base-content mb-4">15. Severability</h2>
            <p className="text-base-content/80 leading-relaxed">
              If any provision of this Agreement is held unenforceable, it will be modified to the
              minimum extent necessary to make it enforceable. The remaining provisions continue in
              full force and effect.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-base-content mb-4">16. Entire Agreement</h2>
            <p className="text-base-content/80 leading-relaxed">
              This Agreement, together with the Privacy Policy at{' '}
              <a
                href="https://empathease.app/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline"
              >
                https://empathease.app/privacy
              </a>
              , constitutes the entire agreement between you and EmpathEase regarding the App.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-base-content mb-4">
              17. Confidentiality of Sessions
            </h2>

            <h3 className="text-lg font-semibold text-base-content mt-4 mb-2">
              17.1 Your obligation
            </h3>
            <p className="text-base-content/80 leading-relaxed">
              Empathy circles and 1:1 sessions depend on participants trusting one another. You
              agree that you will not disclose, publish, repeat, transmit, or otherwise share
              outside of a session any information another participant shares during it — including
              their identity, personal details, or the content of what they said. You further agree
              not to record, screenshot, or transcribe any session. You remain free to share your
              own contributions.
            </p>

            <h3 className="text-lg font-semibold text-base-content mt-4 mb-2">
              17.2 Breach is prohibited conduct
            </h3>
            <p className="text-base-content/80 leading-relaxed">
              Violating Section 17.1 is a material breach of this Agreement and grounds for
              immediate suspension or termination of your account under Section 14 (Termination), in
              addition to any other remedies available.
            </p>

            <h3 className="text-lg font-semibold text-base-content mt-4 mb-2">
              17.3 No guarantee — assumption of risk
            </h3>
            <p className="text-base-content/80 leading-relaxed">
              EmpathEase fosters and enforces confidentiality between participants, but{' '}
              <strong>
                EmpathEase cannot and does not guarantee that other participants will honor it
              </strong>
              . Other participants are independent users whose conduct EmpathEase does not control.{' '}
              <strong>
                You participate, and choose what to disclose, at your own discretion and risk.
              </strong>{' '}
              EmpathEase is not liable for any disclosure of your information by another
              participant. Share only what you are comfortable having another person hear.
            </p>

            <h3 className="text-lg font-semibold text-base-content mt-4 mb-2">
              17.4 Peer support, not treatment
            </h3>
            <p className="text-base-content/80 leading-relaxed">
              Sessions are peer-to-peer practice, not therapy, counseling, or medical care, and
              participants are not licensed professionals acting in that capacity. See Section 10
              (Disclaimers).
            </p>

            <h3 className="text-lg font-semibold text-base-content mt-4 mb-2">17.5 Reporting</h3>
            <p className="text-base-content/80 leading-relaxed">
              If another participant breaches Section 17.1, report it in the app. EmpathEase will
              review reports and may suspend or remove the account.{' '}
              <strong>EmpathEase&apos;s enforcement power is limited to this platform</strong> — it
              cannot compel removal of content published on third-party services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-base-content mb-4">18. Contact Us</h2>
            <p className="text-base-content/80 leading-relaxed">
              If you have questions about this Agreement, please contact us at:
            </p>
            <ul className="list-none pl-0 mt-3 space-y-1 text-base-content/80">
              <li>
                Legal / Privacy:{' '}
                <a href="mailto:privacy@empathease.app" className="text-primary underline">
                  privacy@empathease.app
                </a>
              </li>
              <li>
                Billing / Support:{' '}
                <a href="mailto:support@empathease.app" className="text-primary underline">
                  support@empathease.app
                </a>
              </li>
              <li>
                Website:{' '}
                <a
                  href="https://empathease.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline"
                >
                  https://empathease.app
                </a>
              </li>
            </ul>
          </section>

          <div className="mt-12 pt-8 border-t border-(--ee-tw-gray-200-line)">
            <p className="text-sm text-(--ee-public-ink-50)">Effective Date: June 26, 2026</p>
            <p className="text-sm text-(--ee-public-ink-50) mt-2">
              Applies to: iOS (App Store), Android (Google Play), and Web. For questions about this
              Agreement, contact us at{' '}
              <a href="mailto:privacy@empathease.app" className="underline">
                privacy@empathease.app
              </a>
              .
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TermsOfUse;
